import { useState } from 'react';
import { useAnswerSurveyQuestion } from '@/pages/Mail/hooks/useAnswerSurveyQuestion';
import { useFinishSurvey } from '@/pages/Mail/hooks/useFinishSurvey';
import { useSurveyQuestion } from '@/pages/Mail/hooks/useSurveyQuestion';
import { SurveyQuestionType } from '@/types/components';
import type { SurveyAnswerRequest } from '@/types/survey';

interface FillAnswer {
  selectedOptions: number[];
  textValue: string;
  isOtherAnswerSelected: boolean;
}

const EMPTY_ANSWER: FillAnswer = {
  selectedOptions: [],
  textValue: '',
  isOtherAnswerSelected: false,
};

const FREE_FORM_MAX_LENGTH = 500;
const OTHER_ANSWER_MAX_LENGTH = 250;

interface Params {
  surveyId: string;
  questionIds: string[];
  onSubmitted: () => void;
  onError: () => void;
}

export const useSurveyFill = ({
  surveyId,
  questionIds,
  onSubmitted,
  onError,
}: Params) => {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, FillAnswer>>({});
  const [sentRequests, setSentRequests] = useState<Record<number, string>>({});
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);

  const questionId = questionIds[questionIndex] ?? null;
  const answer = answers[questionIndex] ?? EMPTY_ANSWER;

  const { data: question, isLoading: isQuestionLoading } =
    useSurveyQuestion(questionId);
  const { answerQuestion, isAnswering } = useAnswerSurveyQuestion({ onError });
  const { finishSurvey, isFinishing } = useFinishSurvey({
    surveyId,
    onSuccess: onSubmitted,
    onError,
  });

  const isAnswerFilled =
    answer.textValue.length > 0 ||
    (!answer.isOtherAnswerSelected && answer.selectedOptions.length > 0);
  const maxAnswerLength =
    question?.type === SurveyQuestionType.FreeForm
      ? FREE_FORM_MAX_LENGTH
      : OTHER_ANSWER_MAX_LENGTH;
  const remainingChars = maxAnswerLength - answer.textValue.length;
  const isOverCharLimit = remainingChars < 0;
  const selectedRating = Number(answer.textValue) || 0;

  const updateAnswer = (next: FillAnswer) => {
    setAnswers((previous) => ({ ...previous, [questionIndex]: next }));
  };

  const selectSingleOption = (orderNumber: number) => {
    updateAnswer({
      selectedOptions: [orderNumber],
      textValue: '',
      isOtherAnswerSelected: false,
    });
  };

  const selectRating = (rating: number) => {
    const isSameRating = selectedRating === rating;

    updateAnswer({
      selectedOptions: [],
      textValue: isSameRating ? '' : `${rating}`,
      isOtherAnswerSelected: false,
    });
  };

  const toggleOption = (orderNumber: number) => {
    const isSelected = answer.selectedOptions.includes(orderNumber);
    const selectedOptions = isSelected
      ? answer.selectedOptions.filter((option) => option !== orderNumber)
      : [...answer.selectedOptions, orderNumber];

    updateAnswer({ ...answer, selectedOptions });
  };

  const setTextValue = (textValue: string) => {
    updateAnswer({ ...answer, textValue });
  };

  const toggleOtherAnswer = () => {
    const isOtherAnswerSelected = !answer.isOtherAnswerSelected;
    const isSingleAnswer = question?.type === SurveyQuestionType.SingleAnswer;

    updateAnswer({
      selectedOptions:
        isSingleAnswer && isOtherAnswerSelected ? [] : answer.selectedOptions,
      textValue: '',
      isOtherAnswerSelected,
    });
  };

  const goToNextQuestion = () => {
    const isLastQuestion = questionIndex === questionIds.length - 1;

    if (isLastQuestion) {
      setIsSubmitConfirmOpen(true);
      return;
    }

    setQuestionIndex((previous) => previous + 1);
  };

  const goNext = () => {
    if (!questionId) {
      return;
    }

    const answerRequest: SurveyAnswerRequest = {
      surveyId,
      questionId,
      textValue: answer.textValue,
      selectedAnswerOptions: answer.selectedOptions,
      isOtherAnswer: answer.isOtherAnswerSelected,
    };
    const requestBody = JSON.stringify(answerRequest);
    const isAnswerAlreadySent = sentRequests[questionIndex] === requestBody;

    if (isAnswerAlreadySent) {
      goToNextQuestion();
      return;
    }

    answerQuestion(answerRequest, {
      onSuccess: () => {
        setSentRequests((previous) => ({
          ...previous,
          [questionIndex]: requestBody,
        }));
        goToNextQuestion();
      },
    });
  };

  const goBack = () => {
    setQuestionIndex((previous) => previous - 1);
  };

  const closeSubmitConfirm = () => {
    setIsSubmitConfirmOpen(false);
  };

  return {
    question,
    questionNumber: questionIndex + 1,
    totalQuestions: questionIds.length,
    answer,
    maxAnswerLength,
    remainingChars,
    selectedRating,
    isQuestionLoading,
    isAnswering,
    isFinishing,
    isOverCharLimit,
    isAnswerFilled,
    isFirstQuestion: questionIndex === 0,
    isSubmitConfirmOpen,
    selectSingleOption,
    selectRating,
    toggleOption,
    setTextValue,
    toggleOtherAnswer,
    goNext,
    goBack,
    finishSurvey,
    closeSubmitConfirm,
  };
};
